const page = /* groq */`
  "type": _type,
  "slug": slug.current,
`;

// construct image
export const imageMeta = /* groq */`
  _key,
  _type,
  "url": asset->url,
  "alt": coalesce(alt, asset->altText),
  asset,
  crop,
  customRatio,
  hotspot,
  "id": asset->assetId,
  "type": asset->mimeType,
  "metadata": asset->metadata,
  "width": asset->metadata.dimensions.width,
  "height": asset->metadata.dimensions.height,
  "aspectRatio": asset->metadata.dimensions.aspectRatio,
  "lqip": asset->metadata.lqip
`;
// construct muxVideo
export const muxVideo = /* groq */`
  title,
  "playbackId": video.asset->playbackId,
  "asset": video.asset->
`;

// portable text query
export const ptContent = /* groq */`
  ...,
  markDefs[]{
    ...,
    _type == "link" => {
      "url": @.url,
      "isButton": @.isButton,
      "styles": @.styles{btnColor},
      "page":@.page->{
        ${page}
      }
    },
    _type == "color" => {
      "color": hex
    }
  },
  _type == "image" => {
    ${imageMeta}
  },
`;
export const blocks = /* groq */`
  _type == 'block' => {
    _type,
    _key,
    ${ptContent}
  },
  _type == "muxVideo" => {
    _type,
    _key,
    ${muxVideo}
  },
  _type == "freeform" => {
    _type,
    _key,
    content[]{
      ${ptContent}
    }
  },
  _type == "image" => {
    _type,
    _key,
    ${imageMeta}
  }
`;
export const column = /* groq */`
  styles{
    ...,
    "backgroundColor": backgroundColor.hex,
    "color": color.hex,
  },
  mobileStyles{
    styles,
    ...
  },
  content[]{
    ${blocks}
  }
`;
export const styles = /* groq */`
  backgroundType,
  defined(backgroundImage) => {
    "backgroundImage": backgroundImage{ ${imageMeta} }
  },
  defined(backgroundColor) => {
    "backgroundColor": backgroundColor.hex,
  },
  defined(fontColor) => {
    "color": fontColor.hex,
  },
  mobile{
    backgroundType,
    defined(backgroundImage) => {
      "backgroundImage": backgroundImage{ ${imageMeta} }
    },
    defined(backgroundColor) => {
      "backgroundColor": backgroundColor.hex,
    },
    defined(fontColor) => {
      "color": fontColor.hex,
    },
  },
`;
const cta = /* groq */`
  cta{
    text,
    link,
    legal[]{
      ${ptContent}
    },
    miscType,
    misc[]{
      ${ptContent}
    },
    ratings,
    styles{
      ...,
      "borderColor": borderColor.hex,
      "backgroundColor": backgroundColor.hex,
      "color": fontColor.hex,
    },
    caption[]{
      ${ptContent}
    },
  }
`;
const howItWorks = `
  _type == 'howItWorks' => {
    _type,
    _key,
    id,
    sectionLayout,
    header,
    cards,
    steps,
    enhancedImage,
    image{
      ${imageMeta}
    },
    styles{
      ${styles}
    }
  }
`;
export const partnershipMods = /* groq */`
  ${howItWorks},
  _type == 'hero' => {
    _type,
    _key,
    id,
    content{
      ...,
      image{
        ${imageMeta}
      },
      defined(mobileImage) => {
        "mobileImage": mobileImage{
          ${imageMeta}
        }
      },
      ${cta},
    },
    layout,
    styles{
      ${styles}
    }
  },
  _type == 'feature1' => {
    _type,
    _key,
    content{
      ...,
      image{
        ${imageMeta}
      },
    },
    layout,
    styles{
      ${styles}
    }
  },
  _type == 'feature2' => {
    _type,
    _key,
    content,
    styles{
      ${styles}
    }
  },
  _type == 'grid' => {
    _type,
    _key,
    desktop{
      size,
      columns[]{
        _key,
        _type,
        content[0]{
          _type == "complexBlockObject" => {
            _type,
            _key,
            blocks[]{
              ${ptContent}
            }
          },
          _type == 'table' => {
            ...
          },
          _type == "image" => {
            _type,
            _key,
            ${imageMeta}
          }
        },
        styles,
      },
    },
    mobile{
      isMobileGrid,
      size,
      columns[]{
        _key,
        _type,
        content[0]{
          _type == "complexBlockObject" => {
            _type,
            _key,
            blocks[]{
              ${ptContent}
            }
          },
          _type == 'table' => {
            ...
          },
          _type == "image" => {
            _type,
            _key,
            ${imageMeta}
          }
        },
        styles,
      },
    },
    styles{
      ...,
      backgroundType,
      backgroundImage,
      "bgImage": backgroundImage->url,
      "backgroundColor": backgroundColor.hex,
      "color": fontColor.hex
    }
  },
  _type == 'staticFeatures' => {
    _type,
    _key,
    feature,
    styles{
      ${styles}
    }
  },
  _type == 'ctaSection' => {
    _type,
    _key,
    content{
      heading,
      ${cta},
      caption,
    },
    styles{
      ${styles}
    }
  },
  _type == 'tabSection' => {
    _type,
    _key,
    header,
    tabs[]{
      ...,
      content[]{
        _type == 'table' => {
          ...
        },
        _type == 'grid' => {
          _type,
          _key,
          desktop{
            size,
            columns[]{
              _type == "complexBlockObject" => {
                _type,
                _key,
                content[]{
                  ${ptContent}
                }
              },
              _type == "image" => {
                _type,
                _key,
                ${imageMeta}
              }
            },
          },
          mobile{
            isMobileGrid,
            size,
            columns[]{
              _type == "complexBlockObject" => {
                _type,
                _key,
                content[]{
                  ${ptContent}
                }
              },
              _type == "image" => {
                _type,
                _key,
                ${imageMeta}
              }
            },
          },
          styles{
            ...,
            backgroundType,
            backgroundImage,
            "bgImage": backgroundImage->url,
            "backgroundColor": backgroundColor.hex,
            "color": fontColor.hex
          }
        },
      }
    },
    mobile{
      ...,
      tabs[]{
        ...,
        content[]{
          _type == 'table' => {
            ...
          },
          _type == 'grid' => {
            _type,
            _key,
            desktop{
              size,
              columns[]{
                _type == "complexBlockObject" => {
                  _type,
                  _key,
                  content[]{
                    ${ptContent}
                  }
                },
                _type == "image" => {
                  _type,
                  _key,
                  ${imageMeta}
                }
              },
            },
            mobile{
              isMobileGrid,
              size,
              columns[]{
                _type == "complexBlockObject" => {
                  _type,
                  _key,
                  content[]{
                    ${ptContent}
                  }
                },
                _type == "image" => {
                  _type,
                  _key,
                  ${imageMeta}
                }
              },
            },
            styles{
              ...,
              backgroundType,
              backgroundImage,
              "bgImage": backgroundImage->url,
              "backgroundColor": backgroundColor.hex,
              "color": fontColor.hex
            }
          },
        }
      },
    },
    styles{
      ${styles}
    }
  },
	_type == "testimonials" => {
    _type,
    _key,
    styles{
      "background": "center / cover no-repeat url('" + backgroundImage.asset->url + "')",
      "backgroundColor": backgroundColor.hex
    },
    testimonialsStyles{
      "backgroundColor": backgroundColor.hex,
      "borderColor": borderColor.hex,
    },
    "starColor": testimonialsStyles.starColor.hex,
    header,
    testimonials
  },
	_type == "widget" => {
    _type,
    _key,
    divId,
    externalJs,
    "uploadedFile": uploadedFile.asset->url,
    "bgColor": bgColor.hex
  },
  _type == 'layout' => {
    _type,
    _key,
    header,
    footer
  }
`;
export const modules = /* groq */`
  _type == 'hero' => {
    _type,
    _key,
    content{
      ...,
      cta{
        text,
        link,
        styles{
          "backgroundColor": backgroundColor.hex,
          "color": fontColor.hex,
          "fontSize": fontSize,
          "fontWeight": fontWeight,
        }
      }
    },
    layout,
    styles{
      ${styles}
    }
  },
  _type == 'divider' => {
    _type,
    _key,
    content[]{
      ${blocks},
    },
    styles
  },
  _type == "grid" => {
    _type,
    _key,
    styles{
      ...,
      "backgroundColor": backgroundColor.hex,
      "color": color.hex
    },
    mobileStyles,
    columns[]{
      ${column}
    }
  },
  _type == "testimonials" => {
    _type,
    _key,
    styles{
      "background": "center / cover no-repeat url('" + backgroundImage.asset->url + "')",
      "backgroundColor": backgroundColor.hex
    },
    testimonialsStyles{
      "backgroundColor": backgroundColor.hex,
      "borderColor": borderColor.hex,
    },
    "starColor": testimonialsStyles.starColor.hex,
    header,
    testimonials
  },
  _type == "widget" => {
    _type,
    _key,
    divId,
    externalJs,
    "uploadedFile": uploadedFile.asset->url,
    "bgColor": bgColor.hex
  }
`;
export const lander = /* groq */`
  "type": _type,
  "slug": slug.current,
  modules[]{
    defined(_ref) => {
      ...@->content[0]{
        ${partnershipMods}
      }
    },
    !defined(_ref) => {
      ${partnershipMods}
    }
  },
  title,
  layout{
    _type,
    header{
      ...,
      "partnershipLogo": partnershipLogo.asset->url
    },
    footer{
      ...,
      styles{
        ${styles}
      }
    }
  },
  seo{
    _type,
    "description": metaDesc,
    "title": metaTitle,
    "image": metaImage.asset->url
  }
`;